import { getToken, getImpersonationHeader } from "../../lib/auth";
import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  from,
  split,
} from "@apollo/client";
import { createUploadLink } from "apollo-upload-client";

const cache = new InMemoryCache({});
export const COACH_TOOLS_API = "coachTools";

// Simple link to add operation name as query parameter for network tab visibility
const operationNameLink = new ApolloLink((operation, forward) => {
  const operationName = operation.operationName;

  if (operationName) {
    // Get the base URI based on the API context
    const { api, usePublicGraphql } = operation.getContext();
    let baseUri;
    if (api === COACH_TOOLS_API) {
      const baseUrl = process.env.REACT_APP_PGA_BACKEND_GRAPHQL_ENDPOINT;
      if (baseUrl) {
        baseUri = usePublicGraphql
          ? `${baseUrl}/public-graphql`
          : `${baseUrl}/graphql`;
      } else {
        baseUri = null;
      }
    } else {
      baseUri = process.env.REACT_APP_V3_API_HOST;
    }

    if (baseUri) {
      try {
        const url = new URL(baseUri);
        url.searchParams.set("op", operationName);
        operation.setContext({
          ...operation.getContext(),
          uri: url.toString(),
        });
      } catch (e) {
        // If URL parsing fails, continue without modification
        console.warn("Failed to parse URI for operation name", e);
      }
    }
  }

  return forward(operation);
});

const getEndpointUri = (operation) => {
  const { api, uri, usePublicGraphql } = operation.getContext();

  if (uri) {
    return uri;
  }

  if (api === COACH_TOOLS_API) {
    // COACH_TOOLS_API uses the PGA backend endpoint
    // usePublicGraphql flag determines /graphql vs /public-graphql
    const baseUrl = process.env.REACT_APP_PGA_BACKEND_GRAPHQL_ENDPOINT;
    if (baseUrl) {
      return usePublicGraphql
        ? `${baseUrl}/public-graphql`
        : `${baseUrl}/graphql`;
    }
    return null;
  }

  return process.env.REACT_APP_V3_API_HOST;
};

export const splitLink = split(
  (operation) => {
    const { api } = operation.getContext();
    // Route COACH_TOOLS_API to the first link
    return api === COACH_TOOLS_API;
  },
  createUploadLink({
    uri: (operation) => {
      return getEndpointUri(operation);
    },
  }),
  createUploadLink({
    uri: (operation) => {
      return getEndpointUri(operation);
    },
  }),
);

export const addAuthToken = (token) =>
  token ? { Authorization: `Bearer ${token}` } : {};

export const authContext =
  (tokenLoader) =>
  async (_operation, { headers }) => {
    let token;
    try {
      token = await tokenLoader();
    } catch (e) {
      throw new Error(e);
    }

    return {
      headers: {
        ...headers,
        ...addAuthToken(token),
        ...getImpersonationHeader(),
        "X-Client-Name": "jobs-pga-org",
      },
    };
  };

const authLink = new ApolloLink(async (operation, forward) => {
  const headers = operation.getContext().headers || {};
  const authHeader = await authContext(getToken)(operation, { headers });
  operation.setContext({ headers: authHeader.headers });
  return forward(operation);
});

const apolloClient = new ApolloClient({
  link: from([authLink, operationNameLink, splitLink]),
  cache,
});

export default apolloClient;
